import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnInit,
} from '@angular/core';
import {BiolinkEditorService} from '../../biolink-editor.service';
import {FormBuilder} from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {ActiveAppSectionService} from '../../../../active-app-section.service';
import {animate, style, transition, trigger} from '@angular/animations';
import {debounceTime, map, startWith} from 'rxjs/operators';
import {BiolinkLink} from '../../biolink-link';

@Component({
    selector: 'biolink-editor-link',
    templateUrl: './biolink-editor-link.component.html',
    styleUrls: ['./biolink-editor-link.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideOutPanelAnimation', [
            transition(':enter', [
                style({height: '0px', opacity: 0}),
                animate(
                    '225ms cubic-bezier(0.4,0.0,0.2,1)',
                    style({height: '*', opacity: 1})
                ),
            ]),
            transition(':leave', [
                style({height: '*', opacity: 1}),
                animate(
                    '225ms cubic-bezier(0.4,0.0,0.2,1)',
                    style({height: '0px', opacity: 0})
                ),
            ]),
        ]),
    ],
})
export class BiolinkEditorLinkComponent implements OnInit, OnChanges {
    @Input() link: BiolinkLink;
    activeSlideOutPanel$ = new BehaviorSubject<string>(null);

    form = this.fb.group({
        active: true,
        animation: null,
        leap_until: null,
        image: null,
        expires_at: null,
        activates_at: null,
    });
    leapActive$: Observable<boolean>;
    animationActive$: Observable<boolean>;
    imageActive$: Observable<boolean>;
    scheduleActive$: Observable<boolean>;
    private formSub: Subscription;
    private animSub: Subscription;

    constructor(
        public editor: BiolinkEditorService,
        private fb: FormBuilder,
        private section: ActiveAppSectionService
    ) {}

    // need to update form in "ngOnChanges" as disabled attribute
    // might change in backend when biolink is reloaded
    ngOnChanges() {
        this.formSub?.unsubscribe();
        this.animSub?.unsubscribe();
        this.form.patchValue(this.link);

        if (this.link.active_locked) {
            this.form.get('active').disable();
        } else {
            this.form.get('active').enable();
        }

        // need to remove old sub and resubscribe so there's no infinite loops
        this.formSub = this.form.valueChanges
            .pipe(debounceTime(400))
            .subscribe(() => {
                this.editor.updateContentItemConfig(
                    this.link,
                    this.getDirtyFromValues()
                );
            });

        this.animSub = this.form.get('animation').valueChanges.subscribe(() => {
            this.activeSlideOutPanel$.next(null);
        });
    }

    ngOnInit() {
        this.leapActive$ = this.activeObservable('leap_until');
        this.animationActive$ = this.activeObservable('animation');
        this.imageActive$ = this.activeObservable('image');
        this.scheduleActive$ = combineLatest(
            this.activeObservable('expires_at'),
            this.activeObservable('activates_at')
        ).pipe(map(v => v[0] || v[1]));
    }

    toggleAnimationPanel(name: string) {
        const current = this.activeSlideOutPanel$.value;
        if (current === name) {
            this.activeSlideOutPanel$.next(null);
        } else {
            this.activeSlideOutPanel$.next(name);
        }
    }

    openEditLinkDialog() {
        this.editor.openCrupdateLinkDialog(this.link);
    }

    getParentRoute(): string {
        return this.section.admin ? 'admin' : 'dashboard';
    }

    enableLeapLink() {
        this.form.patchValue({
            leap_link: true,
        });
    }

    clearFormField(fieldName: string) {
        this.form.patchValue({[fieldName]: null});
    }

    private activeObservable(name: string) {
        return this.form.get(name).valueChanges.pipe(
            startWith(this.link[name]),
            map(v => !!v)
        );
    }

    private getDirtyFromValues() {
        const dirtyValues = {};
        Object.entries(this.form.controls).forEach(([name, control]) => {
            if (control.dirty) {
                dirtyValues[name] = control.value;
            }
        });
        return dirtyValues;
    }
}
